import PropTypes from 'prop-types';
import { createRef } from 'react';

import { defineMessages, injectIntl } from 'react-intl';

import classNames from 'classnames';

import ImmutablePropTypes from 'react-immutable-proptypes';
import ImmutablePureComponent from 'react-immutable-pure-component';

import { length } from 'stringz';


import { IconButton } from 'mastodon/components/icon_button';
import SubmitIcon from "mastodon/components/submit_icon";

import AutosuggestTextarea from '../../../components/autosuggest_textarea';
import EmojiPickerDropdown from '../containers/emoji_picker_dropdown_container';
import UploadButtonContainer from '../containers/upload_button_container';
import WarningContainer from '../containers/warning_container';
import { countableText } from '../util/counter';

import { EditIndicator } from './edit_indicator';
import { NavigationBar } from './navigation_bar';
import { PollForm } from "./poll_form";
import { ReplyIndicator } from './reply_indicator';
import { UploadForm } from './upload_form';
import { CloseIcon } from 'mastodon/components/icons/close';
import { SendIcon } from 'mastodon/components/icons/send';
import { Avatar } from 'mastodon/components/avatar';
import { me } from 'mastodon/initial_state';
import { connect } from 'react-redux';
import { cancelReplyCompose } from 'mastodon/actions/compose';
import { closeCommentForm, closeReplyCommentForm } from 'mastodon/actions/comment_form';

const allowedAroundShortCode = '><\u0085\u0020\u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u202f\u205f\u3000\u2028\u2029\u0009\u000a\u000b\u000c\u000d';

const messages = defineMessages({
  placeholder: { id: 'compose_form.placeholder', defaultMessage: 'What is on your mind?' },
  spoiler_placeholder: { id: 'compose_form.spoiler_placeholder', defaultMessage: 'Content warning (optional)' },
  publish: { id: 'compose_form.publish', defaultMessage: 'Post' },
  saveChanges: { id: 'compose_form.save_changes', defaultMessage: 'Update' },
  reply: { id: 'compose_form.reply', defaultMessage: 'Reply' },
  replyingTo: { id: 'compose_form.replying_to', defaultMessage: 'Replying to: {name}' },
  sendComment: { id: 'compose_form.send_comment', defaultMessage: 'Submit a comment' },
});

const mapStateToProps = (state) => ({
  account: me ? state.getIn(['accounts', me]) : undefined,
  editReplyAccountNames: state.getIn(['commentForm', 'replyAccountNames']),
  editReplyDisplayNames: state.getIn(['commentForm', 'replyDisplayNames']),
  accounts: state.getIn(['accounts']),
});
class CommentForm extends ImmutablePureComponent {
  static propTypes = {
    replyToId: PropTypes.string,
    replyAccountName: PropTypes.string,
    replyDisplayName: PropTypes.string,
    expandReply: PropTypes.func,
    dispatch: PropTypes.func.isRequired,
    isReply: PropTypes.bool,
    intl: PropTypes.object.isRequired,
    text: PropTypes.string.isRequired,
    suggestions: ImmutablePropTypes.list,
    spoiler: PropTypes.bool,
    privacy: PropTypes.string,
    spoilerText: PropTypes.string,
    focusDate: PropTypes.instanceOf(Date),
    caretPosition: PropTypes.number,
    preselectDate: PropTypes.instanceOf(Date),
    isSubmitting: PropTypes.bool,
    isChangingUpload: PropTypes.bool,
    isEditing: PropTypes.bool,
    isUploading: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClearSuggestions: PropTypes.func.isRequired,
    onFetchSuggestions: PropTypes.func.isRequired,
    onSuggestionSelected: PropTypes.func.isRequired,
    onChangeSpoilerText: PropTypes.func.isRequired,
    onPaste: PropTypes.func.isRequired,
    onPickEmoji: PropTypes.func.isRequired,
    autoFocus: PropTypes.bool,
    withoutNavigation: PropTypes.bool,
    anyMedia: PropTypes.bool,
    missingAltText: PropTypes.bool,
    isInReply: PropTypes.bool,
    singleColumn: PropTypes.bool,
    lang: PropTypes.string,
    maxChars: PropTypes.number,
    isReplyComment: PropTypes.bool,
    onClearCompose: PropTypes.func.isRequired,
    account: ImmutablePropTypes.map,
    editReplyAccountName: PropTypes.string,
    editReplyDisplayName: PropTypes.string
  };

  static defaultProps = {
    autoFocus: false,
  };

  state = {
    highlighted: false,
    hasMedia: false,
    width: window.innerWidth,
    hasMoreThanChars: false,
    actualMention: '',
  };

  constructor(props) {
    super(props);
    this.textareaRef = createRef(null);
    this.mentionMap = [];
  }

  initializeTextWithMention = () => {
    const { replyAccountName, replyDisplayName, text, onChange } = this.props;
    if (replyAccountName && replyDisplayName) {
      const displayMention = `@${replyDisplayName}`;
      const actualMention = `@${replyAccountName}`;

      this.setState({ actualMention });

      if (!text.startsWith(`${actualMention} `) && !text.includes(actualMention)) {
        const newText = `${displayMention} ${text}`.trim() + ' ';
        onChange(newText);
      }
    }
  };

  handleChange = (e) => {
    this.props.onChange(e.target.value);
  };

  handleKeyDown = (e) => {
    if (e.keyCode === 13 && (e.ctrlKey || e.metaKey)) {
      this.handleSubmit();
    }
  };

  handleMediaChange = (value) => {
    this.setState({ hasMedia: value });
  };

  getFulltextForCharacterCounting = () => {
    return [this.props.spoiler ? this.props.spoilerText : '', countableText(this.props.text, this.mentionMap)].join('');
  };

  canSubmit = () => {
    const { isSubmitting, isChangingUpload, isUploading, anyMedia, maxChars } = this.props;
    const fulltext = this.getFulltextForCharacterCounting();
    const isOnlyWhitespace = fulltext.trim().length === 0;

    return !(
      isSubmitting ||
      isUploading ||
      isChangingUpload ||
      length(fulltext) > maxChars ||
      (isOnlyWhitespace && !anyMedia) ||
      (!fulltext && !anyMedia)
    );
  };

  handleSubmit = (e) => {
    const { text, replyAccountName, replyDisplayName, editReplyAccountNames, editReplyDisplayNames, onChange, onSubmit, isReplyComment } = this.props;
    const { actualMention } = this.state;

    if (text !== this.textareaRef.current.value) {
      onChange(this.textareaRef.current.value);
    }

    let finalText = text;

    if (Array.isArray(editReplyAccountNames) && Array.isArray(editReplyDisplayNames)) {
      editReplyAccountNames.forEach((accountName, index) => {
        const displayName = editReplyDisplayNames[index];
        const actual = `@${accountName}`;
        const display = `@${displayName}`;

        if (finalText.includes(display)) {
          finalText = finalText.replaceAll(display, actual);
        }
      });

      const missingAccounts = editReplyAccountNames
        .map(accountName => `@${accountName}`)
        .filter(actual => !finalText.includes(actual));

      if (missingAccounts.length > 0) {
        finalText = missingAccounts.join(' ') + ' ' + finalText;
        finalText = finalText.trim();
      }
    }

    if (isReplyComment && replyAccountName && replyDisplayName) {
      const displayMention = `@${replyDisplayName}`;
      if (text.includes(displayMention) && actualMention) {
        finalText = text.replace(displayMention, actualMention);
      } else if (!text.includes(actualMention) && actualMention) {
        finalText = `${actualMention} ${finalText}`.trim();
      }
    }

    if (!this.canSubmit()) {
      return;
    }

    onSubmit({ isComment: true, finalText: actualMention ? finalText : undefined, composeOriginId: window.location.pathname.split("/").pop() });
    document.body.classList.remove('with-modals--active');

    this.props.dispatch(cancelReplyCompose());
    this.props.dispatch(closeCommentForm());
    this.props.dispatch(closeReplyCommentForm());

    if (this.props.expandReply) {
      this.props.expandReply(this.props.replyToId, true);
    }

    if (e) {
      e.preventDefault();
    }
  };

  onSuggestionsClearRequested = () => {
    this.props.onClearSuggestions();
  };

  onSuggestionsFetchRequested = (token) => {
    this.props.onFetchSuggestions(token);
  };

  onSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['text']);
    if (value && value.type === 'account' && value.id) {
      let account = this.props.accounts.get(value.id);
      if (account) {
        this.mentionMap.push({
          display: account.get('display_name'),
          username: account.get('acct') || account.get('username'),
        });
      }
    } 
  };

  onSpoilerSuggestionSelected = (tokenStart, token, value) => {
    this.props.onSuggestionSelected(tokenStart, token, value, ['spoiler_text']);
  };

  handleChangeSpoilerText = (e) => {
    this.props.onChangeSpoilerText(e.target.value);
  };

  handleFocus = () => {
    if (this.composeForm && !this.props.singleColumn) {
      const { left, right } = this.composeForm.getBoundingClientRect();
      if (left < 0 || right > (window.innerWidth || document.documentElement.clientWidth)) {
        this.composeForm.scrollIntoView();
      }
    }
  };
  handleResize() {
    this.setState({ width: window.innerWidth });
  }


  componentDidMount() {
    if (!this.props.isEditing) {
      this.initializeTextWithMention();
    } else {
      this.replaceActualMentionWithDisplay();
    }
    this._updateFocusAndSelection({});
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
    window.removeEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isEditing) {
      if (prevProps.replyAccountName !== this.props.replyAccountName) {
        this.initializeTextWithMention();
      }
    } else {
      if (prevProps.text !== this.props.text) {
        this.replaceActualMentionWithDisplay();
      }
    }
    if (prevProps.text !== this.props.text) {
      const hasMoreThanChars = this.props.text?.length > 33;
      this.setState({ hasMoreThanChars });
    }
    this._updateFocusAndSelection(prevProps);
  }

  _updateFocusAndSelection = (prevProps) => {
    if (this.props.focusDate && this.props.focusDate !== prevProps.focusDate) {
      let selectionEnd, selectionStart;

      if (this.props.preselectDate !== prevProps.preselectDate && this.props.isInReply) {
        const mention = this.props.replyDisplayName ? `@${this.props.replyDisplayName}` : `@${this.props.replyAccountName}`;
        selectionStart = mention.length + 1;
        selectionEnd = mention.length + 1;
      } else if (typeof this.props.caretPosition === 'number') {
        selectionStart = this.props.caretPosition;
        selectionEnd = this.props.caretPosition;
      } else {
        selectionEnd = this.props.text.length;
        selectionStart = selectionEnd;
      }

      Promise.resolve().then(() => {
        this.textareaRef.current.setSelectionRange(selectionStart, selectionEnd);
        this.textareaRef.current.focus();
        this.setState({ highlighted: true });
        this.timeout = setTimeout(() => this.setState({ highlighted: false }), 700);
      }).catch(console.error);
    } else if (prevProps.isSubmitting && !this.props.isSubmitting) {
      this.textareaRef.current.focus();
    } else if (this.props.spoiler !== prevProps.spoiler) {
      if (this.props.spoiler) {
        this.spoilerText.input.focus();
      } else if (prevProps.spoiler) {
        this.textareaRef.current.focus();
      }
    }
  };

  replaceActualMentionWithDisplay = () => {
    const { editReplyAccountNames, editReplyDisplayNames, text, onChange } = this.props;

    if (Array.isArray(editReplyAccountNames) && Array.isArray(editReplyDisplayNames)) {
      let updatedText = text;
      let foundActualMention = '';

      editReplyAccountNames.forEach((accountName, index) => {
        const displayName = editReplyDisplayNames[index];

        if (updatedText.includes(`@${accountName}`)) {
          foundActualMention = `@${accountName}`;
          updatedText = updatedText.replaceAll(`@${accountName}`, `@${displayName}`);
        }
      });

      if (foundActualMention) {
        this.setState({ actualMention: foundActualMention });
      }

      if (updatedText !== text) {
        onChange(updatedText);
      }
    }
  };

  setSpoilerText = (c) => {
    this.spoilerText = c;
  };

  setRef = c => {
    this.composeForm = c;
  };

  handleEmojiPick = (data) => {
    const { text } = this.props;
    const position = this.textareaRef.current.selectionStart;
    const needsSpace = data.custom && position > 0 && !allowedAroundShortCode.includes(text[position - 1]);

    this.props.onPickEmoji(position, data, needsSpace);
  };

  handleCloseReply = () => {
    this.props.onClearCompose();
  };

  render() {
    const { intl, onPaste, autoFocus, withoutNavigation } = this.props;
    const { highlighted, width, hasMoreThanChars } = this.state;
    const disabled = this.props.isSubmitting;
    const hasText = this.props.text.trim().length > 0;

    return (
      <div>
        <form className='compose-form' onSubmit={this.handleSubmit}>
          {/* <ReplyIndicator /> */}
          {!withoutNavigation && <NavigationBar />}
          {/* <WarningContainer /> */}

          <div className='compose-form-container' style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end', alignItems: 'center', gap: '5px' }}>
            {/* <Avatar account={this.props.account} size={36} /> */}
            <div className={classNames('compose-form__highlightable', {
              active: highlighted,
              'has-more-than-chars': hasMoreThanChars,
            })} ref={this.setRef}>
              <div className='compose-form__scrollable comment-form'>
                <div className='comment-form__content'>
                  <AutosuggestTextarea
                    ref={this.textareaRef}
                    placeholder={intl.formatMessage(messages.placeholder)}
                    disabled={disabled}
                    value={this.props.text}
                    onChange={this.handleChange}
                    suggestions={this.props.suggestions}
                    onFocus={this.handleFocus}
                    onKeyDown={this.handleKeyDown}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    onSuggestionSelected={this.onSuggestionSelected}
                    onPaste={onPaste}
                    autoFocus={autoFocus}
                    lang={this.props.lang}
                    isReply
                    isComment
                    charCount={this.getFulltextForCharacterCounting().length}
                  />
                  <UploadForm onChangeMedia={this.handleMediaChange} />
                  <PollForm />
                </div>
              </div>
            </div>
          </div>

          <div className='comment-form__upload-submit-containner' style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '5px' }}>
            {/* <EditIndicator />
            {width >= 1025 ? <EmojiPickerDropdown onPickEmoji={this.handleEmojiPick} /> : <UploadButtonContainer />} */}
            <div className='comment-form__upload-submit'>
              <UploadButtonContainer usageType='comment' />

              <div className='compose-form__submit'>
                <button className='compose-form__comment-submit'
                  type='submit'
                  disabled={!this.canSubmit()}
                >
                  <SendIcon {...(!this.canSubmit() ? { color: 'gray' } : { color: 'white' })} {...((hasText || this.state.hasMedia) && { active: true })} />
                  {intl.formatMessage(messages.sendComment)}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

}

export default connect(mapStateToProps)(injectIntl(CommentForm));
